import { useState, useEffect, useMemo } from "react";
import { Search } from "lucide-react";
import { Modal } from "../primitives/Modal";
import { Button } from "../primitives/Button";
import { createMembership, listOrganizations } from "../../api/admin";
import { ROLE_OPTIONS } from "../../api/admin";
import { getRoleDescription } from "../../utils/permissions";
import { cn } from "../utils/cn";

type AddMembershipModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
};

const ORG_TYPE_LABELS: Record<string, string> = {
  xlkey_internal: "XLKey",
  farm: "Ferme",
  ambassador_org: "Ambassadeur",
  drone_pilot_org: "Pilote",
};

export function AddMembershipModal({ open, onClose, onSuccess, userId }: AddMembershipModalProps) {
  const [organizations, setOrganizations] = useState<Array<{ id: string; name: string; type: string; status: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [role, setRole] = useState<string>("ops");
  const [status, setStatus] = useState<"active" | "suspended">("active");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLoading(true);
      listOrganizations()
        .then((data) => {
          // Filter to only active OPERATOR organizations (not client farms)
          // Allowed types: xlkey_internal, ambassador_org, drone_pilot_org
          const allowedTypes = ["xlkey_internal", "ambassador_org", "drone_pilot_org"];
          const activeOrgs = (data.items || []).filter(
            (org: any) => org.status === "active" && allowedTypes.includes(org.type)
          );
          setOrganizations(activeOrgs);
        })
        .catch((error) => {
          console.error("Failed to load organizations:", error);
          setError("Erreur lors du chargement des organisations");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setSelectedOrgId("");
      setRole("operator");
      setStatus("active");
      setError(null);
    }
  }, [open]);

  const filteredOrgs = useMemo(() => {
    if (!searchQuery.trim()) return organizations;
    const query = searchQuery.toLowerCase();
    return organizations.filter(
      (org) =>
        org.name.toLowerCase().includes(query) ||
        ORG_TYPE_LABELS[org.type]?.toLowerCase().includes(query) ||
        org.type.toLowerCase().includes(query)
    );
  }, [organizations, searchQuery]);

  const handleSubmit = async () => {
    if (!selectedOrgId) {
      setError("Veuillez sélectionner une organisation");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createMembership({
        organization_id: selectedOrgId,
        user_id: userId,
        org_role: role,
        status,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de la création de l'accès");
    } finally {
      setSaving(false);
    }
  };

  const selectedOrg = organizations.find((org) => org.id === selectedOrgId);
  const roleInfo = getRoleDescription(role);

  return (
    <Modal
      title="Ajouter un accès"
      open={open}
      onClose={onClose}
      showDefaultButtons={false}
      showCloseButton={true}
      size="lg"
    >
      <div className="space-y-4">
        {/* Help text */}
        <div className="rounded-md border border-border bg-surface-muted p-3">
          <div className="text-xs text-muted">
            <p className="mb-1">
              <strong className="text-text">L'accès donne des permissions au niveau de l'organisation.</strong>
            </p>
            <p>
              La gestion des clients (fermes) se fait dans le module Ambassadeur. Par défaut, un utilisateur a accès à toutes les fermes de son organisation, mais cela peut être modifié.
            </p>
          </div>
        </div>

        {/* Organisation searchable dropdown */}
        <div>
          <label className="text-xs text-muted mb-2 block">Équipe / Organisation opératrice</label>
          <div className="relative">
            <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="text"
              placeholder="Rechercher une organisation opératrice..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-surface text-sm text-text placeholder:text-muted"
            />
          </div>
          {filteredOrgs.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded-md border border-border bg-surface">
              {filteredOrgs.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => setSelectedOrgId(org.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 text-sm transition",
                    selectedOrgId === org.id
                      ? "bg-primary/10 text-primary"
                      : "text-text hover:bg-surface-muted"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{org.name}</div>
                      <div className="text-xs text-muted">{ORG_TYPE_LABELS[org.type] || org.type}</div>
                    </div>
                    {selectedOrgId === org.id && (
                      <span className="text-primary">✓</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          {searchQuery && filteredOrgs.length === 0 && !loading && (
            <div className="mt-2 text-xs text-muted text-center py-2">Aucune organisation opératrice trouvée</div>
          )}
          {!loading && organizations.length === 0 && (
            <div className="mt-2 text-xs text-muted text-center py-2">Aucune organisation opératrice disponible</div>
          )}
        </div>

        {selectedOrg && (
          <>
            {/* Rôle */}
            <div>
              <label className="text-xs text-muted mb-2 block">Rôle</label>
              <select
                className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                {ROLE_OPTIONS.map((r) => {
                  const roleInfo = getRoleDescription(r);
                  return (
                    <option key={r} value={r}>
                      {roleInfo.label}
                    </option>
                  );
                })}
              </select>
              {roleInfo.description && (
                <div className="mt-1 text-xs text-muted">{roleInfo.description}</div>
              )}
            </div>

            {/* Statut */}
            <div>
              <label className="text-xs text-muted mb-2 block">Statut</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStatus("active")}
                  className={cn(
                    "flex-1 px-3 py-2 rounded-md text-sm font-medium transition",
                    status === "active"
                      ? "bg-success/10 text-success border border-success/20"
                      : "bg-surface-muted text-text border border-border"
                  )}
                >
                  Actif
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("suspended")}
                  className={cn(
                    "flex-1 px-3 py-2 rounded-md text-sm font-medium transition",
                    status === "suspended"
                      ? "bg-warning/10 text-warning border border-warning/20"
                      : "bg-surface-muted text-text border border-border"
                  )}
                >
                  Suspendu
                </button>
              </div>
            </div>
          </>
        )}

        {error && (
          <div className="rounded-md border border-danger bg-danger/10 p-3">
            <div className="text-xs text-danger">{error}</div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <Button label="Annuler" intent="default" size="sm" onClick={onClose} disabled={saving} />
          <Button
            label="Ajouter l'accès"
            intent="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={saving || !selectedOrgId}
          />
        </div>
      </div>
    </Modal>
  );
}
